import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Users, Shield, CheckCircle, ArrowRight } from 'lucide-react';
import './HomePage.css';

const HomePage = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: <Briefcase size={40} />,
      title: 'Consulting',
      description: 'Strategic guidance to drive your business forward with proven methodologies and expert insights.',
    },
    {
      icon: <Users size={40} />,
      title: 'Recruitment',
      description: 'Connect with top talent that aligns with your company culture and business objectives.',
    },
    {
      icon: <Shield size={40} />,
      title: 'Background Checks',
      description: 'Comprehensive verification services ensuring security and compliance for your organization.',
    },
  ];

  const stats = [
    { number: '500+', label: 'Clients Served' },
    { number: '10+', label: 'Years Experience' },
    { number: '95%', label: 'Success Rate' },
    { number: '24/7', label: 'Support Available' },
  ];

  return (
    <div className="home-page" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title" data-testid="hero-title">
              Professional Consulting <br />for Growing Businesses
            </h1>
            <p className="hero-subtitle" data-testid="hero-subtitle">
              Transform your organization with expert consulting, strategic recruitment, <br />
              and comprehensive background verification services.
            </p>
            <div className="hero-cta">
              <button 
                className="btn btn-primary btn-lg"
                onClick={() => navigate('/background-check')}
                data-testid="hero-cta-primary"
              >
                Request Background Check
                <ArrowRight size={20} />
              </button>
              <button 
                className="btn btn-secondary btn-lg"
                onClick={() => navigate('/services')}
                data-testid="hero-cta-secondary"
              >
                Explore Services
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card" data-testid={`stat-${index}`}>
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title" data-testid="services-title">Our Services</h2>
            <p className="section-subtitle">
              Comprehensive solutions tailored to your business needs
            </p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card card" data-testid={`service-${index}`}>
                <div className="service-icon">{service.icon}</div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <button 
                  className="service-link"
                  onClick={() => navigate('/services')}
                >
                  Learn More <ArrowRight size={18} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section section">
        <div className="container">
          <div className="why-choose-content">
            <div className="why-choose-text">
              <h2 className="section-title" data-testid="why-choose-title">Why Choose Velonity</h2>
              <p className="section-description">
                We combine industry expertise with innovative approaches to deliver exceptional results for our clients.
              </p>
              <ul className="features-list">
                <li>
                  <CheckCircle size={24} />
                  <div>
                    <strong>Expert Team</strong>
                    <p>Seasoned professionals with decades of combined experience</p>
                  </div>
                </li>
                <li>
                  <CheckCircle size={24} />
                  <div>
                    <strong>Proven Methods</strong>
                    <p>Data-driven strategies that deliver measurable results</p>
                  </div>
                </li>
                <li>
                  <CheckCircle size={24} />
                  <div>
                    <strong>Tailored Solutions</strong>
                    <p>Customized approach for your unique business challenges</p>
                  </div>
                </li>
                <li>
                  <CheckCircle size={24} />
                  <div>
                    <strong>Continuous Support</strong>
                    <p>Dedicated support throughout your engagement with us</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="why-choose-image">
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&auto=format&fit=crop" 
                alt="Professional team meeting"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title" data-testid="cta-title">
              Ready to Get Started?
            </h2>
            <p className="cta-description">
              Submit your background check request today and experience our professional service.
            </p>
            <button 
              className="btn btn-accent btn-lg"
              onClick={() => navigate('/background-check')}
              data-testid="cta-button"
            >
              Submit Background Check Request
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
